import React from "react";
import { Icon } from "@iconify/react";
import { formatPackagePrice } from "../../utils/esim";

function SummaryRow({ label, value, emphasize = false }) {
  if (!value && value !== 0) {
    return null;
  }

  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-sm text-slate-500">{label}</span>
      <span
        className={`text-right text-sm ${
          emphasize ? "font-bold text-[#1E305A]" : "font-medium text-[#1E305A]"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function BookingSummaryPill({ label, value, icon }) {
  if (!value) {
    return null;
  }

  return (
    <div className="rounded-[18px] bg-[#EFF5FF] px-4 py-3">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
        <Icon icon={icon} className="h-4 w-4 text-[#1E61DC]" />
        {label}
      </div>
      <p className="mt-2 text-sm font-semibold text-[#1E305A]">{value}</p>
    </div>
  );
}

function EsimBookingSummaryCard({ bookingResponse }) {
  const details = bookingResponse?.packageDetails;

  if (!details) {
    return null;
  }

  return (
    <aside className="rounded-[24px] border border-[#D8E5FF] bg-white p-5 shadow-sm">
      <div className="flex items-start gap-4">
        {details.countryImage ? (
          <img
            src={details.countryImage}
            alt={details.country || "Country"}
            className="h-14 w-14 rounded-[18px] object-cover"
          />
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-[18px] bg-[#EFF5FF] text-3xl">
            {details.countryFlag}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#1E61DC]">
            Booking summary
          </p>
          <h2 className="mt-2 text-xl font-bold text-[#1E305A]">
            {details.country || "eSIM package"}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            {details.title}
          </p>
          {details.operatorLogo ? (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#EFF5FF] px-3 py-1.5">
              <img
                src={details.operatorLogo}
                alt={details.operator || "Operator"}
                className="h-5 w-5 rounded-full object-cover"
              />
              <span className="text-xs font-semibold text-[#1E305A]">
                {details.operator || "Network operator"}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <BookingSummaryPill
          label="Coverage"
          value={details.country || details.coverage}
          icon="mdi:earth"
        />
        <BookingSummaryPill
          label="Status"
          value={details.status}
          icon="mdi:check-decagram"
        />
      </div>

      <div className="mt-6 rounded-[20px] bg-[#F5F8FF] p-4">
        <div className="space-y-3">
          <SummaryRow label="Operator" value={details.operator} />
          <SummaryRow label="Supplier" value={details.supplier} />
          <SummaryRow label="Data amount" value={details.dataAmount} />
          <SummaryRow label="Validity" value={details.validity} />
          <SummaryRow
            label="Package type"
            value={details.packageType || details.planType}
          />
          <SummaryRow label="eSIM type" value={details.esimType} />
          <SummaryRow label="Quantity" value={details.quantity} />
        </div>

        <div className="my-5 h-px bg-[#D9E4F7]" />

        <div className="space-y-3">
          <SummaryRow
            label="Unit price"
            value={formatPackagePrice(
              details.unitPrice || details.displayPrice,
              details.currency,
            )}
          />
          {Number.isFinite(details.bookingFee) ? (
            <SummaryRow
              label="Booking fee"
              value={formatPackagePrice(details.bookingFee, details.currency)}
            />
          ) : null}
          <SummaryRow
            label="Grand total"
            value={formatPackagePrice(
              details.grandTotal || details.displayPrice,
              details.currency,
            )}
            emphasize
          />
        </div>
      </div>
    </aside>
  );
}

export default EsimBookingSummaryCard;
